import type { PredictorConfig } from "../engine/config";
import { effectiveConfig } from "../engine/config";
import { dateOf, type IsoDate } from "../engine/dates";
import type { Bean, Grinder, Shot } from "../engine/model";
import { classify, fit } from "../engine/predictor";
import { beanSummary, prior, reference, type BeanSummary, type Prior, type Reference } from "../engine/startingPoint";
import type { Repositories } from "../storage/repositories";
import { activeShots } from "./calibration";

/**
 * Builds the inputs the pure engine needs for a starting point: per-bean summaries from each past bean's own shots
 * (no pseudo-observation, so x_ref cannot feed back into itself), then x_ref and the prior for a bean.
 */

/** A past bean's fit as of its last shot on the grinder, if it has any shots there. */
export interface DialedBean {
  readonly bean: Bean;
  readonly lastShotDate: IsoDate;
  readonly xDialed: number;
  readonly bFinal: number;
  readonly nEff: number;
  readonly validShots: number;
}

export async function dialedBeans(repos: Repositories, grinder: Grinder, cfg: PredictorConfig,
                                  excludeBeanId: number | null = null): Promise<DialedBean[]> {
  const out: DialedBean[] = [];
  for (const bean of await repos.beans.findAll()) {
    if (bean.id === null || bean.id === excludeBeanId) continue;
    // Shots before a recalibration of this grinder no longer describe it: leave them out of x_ref and learning.
    const shots = activeShots(await repos.shots.findByBeanAndGrinder(bean.id, grinder.id as number), grinder);
    if (shots.length === 0) continue;
    const lastShotDate = dateOf((shots[0] as Shot).timestamp); // newest first
    const f = fit(grinder, bean, shots, lastShotDate, cfg);
    out.push({ bean, lastShotDate, xDialed: f.prediction.grindSettingRaw, bFinal: f.prediction.slopeUsed, nEff: f.nEff,
               validShots: countValid(shots, bean, cfg) });
  }
  return out;
}

function countValid(shots: readonly Shot[], bean: Bean, cfg: PredictorConfig): number {
  const c = effectiveConfig(cfg, bean);
  return shots.filter((s) => classify((s.timeSeconds * c.targetYieldG) / s.yieldG, c) === "valid").length;
}

/** Summaries of the beans that qualify as dialed in (n_eff ≥ cfg.xDialedMinNeff as of their last shot). */
export async function summariesFor(repos: Repositories, grinder: Grinder, cfg: PredictorConfig,
                                   excludeBeanId: number | null = null): Promise<BeanSummary[]> {
  const offsets = await repos.offsets.load();
  return (await dialedBeans(repos, grinder, cfg, excludeBeanId))
    .filter((d) => d.nEff >= cfg.xDialedMinNeff)
    .map((d) => beanSummary(d.bean, grinder, d.xDialed, d.lastShotDate, offsets, cfg));
}

export async function referenceFor(repos: Repositories, grinder: Grinder, today: IsoDate, cfg: PredictorConfig,
                                   excludeBeanId: number | null = null): Promise<Reference> {
  return reference(grinder, await summariesFor(repos, grinder, cfg, excludeBeanId), today, cfg);
}

/** The prior for `bean` on `grinder` as of `today`, built from every other bean's history. */
export async function priorFor(repos: Repositories, grinder: Grinder, bean: Bean, today: IsoDate,
                               cfg: PredictorConfig): Promise<Prior> {
  const ref = await referenceFor(repos, grinder, today, cfg, bean.id);
  return prior(grinder, bean, ref, await repos.offsets.load(), today, cfg);
}
